import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import debounce from 'lodash.debounce';

/**
 * Custom hook for managing list page logic including search, filter, sort, and pagination
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.service - Service client with getList and search methods
 * @param {string} [config.localStoragePrefix=''] - Prefix for localStorage keys (e.g., 'po', 'zoe')
 * @param {number} [config.debounceMs=500] - Debounce delay in milliseconds
 * @param {number} [config.defaultLimit=20] - Default page limit
 * @param {Function} [config.buildSearchParams] - Custom function to build search params from state
 *
 * @returns {Object} State and handlers for list page functionality
 *
 * @example
 * const {
 *   data,
 *   loading,
 *   searchValue,
 *   setSearchValue,
 *   handleSearchClick,
 *   fetchAll,
 *   // ... other state and handlers
 * } = useListPageLogic({
 *   service: zoeShipmentTrackingClient,
 *   localStoragePrefix: 'zoe',
 *   buildSearchParams: ({ searchValue, filters, dateFilter }) => {
 *     const filterObj = {};
 *     filters.forEach(f => filterObj[f.field] = f.value);
 *     filterObj.TrackingNumber = searchValue;
 *     return filterObj;
 *   }
 * });
 */
export function useListPageLogic(config) {
  const {
    service,
    localStoragePrefix = '',
    debounceMs = 500,
    defaultLimit = 20,
    buildSearchParams
  } = config;

  // State management
  const [data, setData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [sortOrder, setSortOrder] = useState('default');
  const [dateFilter, setDateFilter] = useState('default');
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ field: '', value: '' });
  const [filters, setFilters] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    limit: defaultLimit
  });

  // Restore saved search state from localStorage on mount
  useEffect(() => {
    const savedSearch = localStorage.getItem(`${localStoragePrefix}searchValue`);
    const savedFilters = localStorage.getItem(`${localStoragePrefix}advancedSearchFilters`);

    if (savedSearch) {
      setSearchValue(savedSearch);
    }

    if (savedFilters) {
      try {
        setFilters(JSON.parse(savedFilters));
      } catch (error) {
        console.error('Failed to parse saved filters:', error);
      }
    }
  }, [localStoragePrefix]);

  // Persist search value to localStorage
  useEffect(() => {
    localStorage.setItem(`${localStoragePrefix}searchValue`, searchValue);
  }, [searchValue, localStoragePrefix]);

  // Persist filters to localStorage
  useEffect(() => {
    localStorage.setItem(`${localStoragePrefix}advancedSearchFilters`, JSON.stringify(filters));
  }, [filters, localStoragePrefix]);

  /**
   * Fetch all items without filters
   */
  const fetchAll = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      setError(null);
      try {
        const response = await service.getList(page, limit);

        if (response.success) {
          setData(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setData([]);
          setError(response.message || 'Failed to load data.');
        }
      } catch (err) {
        setData([]);
        setError('Failed to load data. Please try again.');
      }
      setLoading(false);
    },
    [service, pagination.limit]
  );

  /**
   * Use refs to store latest values for debounced search
   * This prevents recreating the debounced function on every render
   */
  const searchParamsRef = useRef({ searchValue, filters, dateFilter, sortBy, sortOrder });
  const previousSearchParamsRef = useRef({ searchValue: '', filters: [], dateFilter: 'default', sortBy: 'default', sortOrder: 'default' });

  useEffect(() => {
    searchParamsRef.current = { searchValue, filters, dateFilter, sortBy, sortOrder };
  }, [searchValue, filters, dateFilter, sortBy, sortOrder]);

  /**
   * Search with filters, sorting, and pagination
   */
  const handleSearch = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true);
      setError(null);
      try {
        const { searchValue, filters, dateFilter, sortBy, sortOrder } = searchParamsRef.current;
        let searchParams;

        // Use custom param builder if provided, otherwise use default
        if (buildSearchParams) {
          searchParams = buildSearchParams({
            searchValue,
            filters,
            dateFilter,
            sortBy,
            sortOrder
          });
        } else {
          // Default behavior: convert filters array to object
          searchParams = {};
          filters.forEach((f) => {
            if (f.field && f.value) {
              searchParams[f.field] = f.value;
            }
          });

          if (dateFilter !== 'default') {
            searchParams.date_filter = dateFilter;
          }

          if (searchValue) {
            searchParams.search_value = searchValue;
          }
        }

        const response = await service.search(
          searchParams,
          page,
          sortBy !== 'default' ? sortBy : undefined,
          sortOrder !== 'default' ? sortOrder : undefined,
          limit
        );

        if (response.success) {
          setData(response.data);
          setPagination({
            ...response.pagination,
            limit,
            total: response.total
          });
        } else {
          setData([]);
          setError(response.message || 'Search failed. Please try again.');
        }
      } catch (err) {
        setData([]);
        setError('Search failed. Please try again.');
      }
      setLoading(false);
    },
    [service, pagination.limit, buildSearchParams]
  );

  /**
   * Debounced search function - now stable because handleSearch doesn't change
   */
  const debouncedSearch = useMemo(
    () =>
      debounce((page, limit) => {
        handleSearch(page, limit);
      }, debounceMs),
    [handleSearch, debounceMs]
  );

  /**
   * Cleanup debounced function on unmount
   */
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Returned so PaginatedControls uses the same search-vs-fetchAll decision
  // as the effect below.
  const hasSearchCriteria =
    !!searchValue ||
    filters.length > 0 ||
    dateFilter !== 'default' ||
    sortBy !== 'default' ||
    sortOrder !== 'default';

  /**
   * Trigger search on filter/sort changes
   */
  useEffect(() => {
    // Check if any search parameter has actually changed
    const prev = previousSearchParamsRef.current;
    const hasChanged =
      searchValue !== prev.searchValue ||
      JSON.stringify(filters) !== JSON.stringify(prev.filters) ||
      dateFilter !== prev.dateFilter ||
      sortBy !== prev.sortBy ||
      sortOrder !== prev.sortOrder;

    if (!hasChanged) {
      return; // Skip if nothing changed
    }

    // Update previous values
    previousSearchParamsRef.current = { searchValue, filters, dateFilter, sortBy, sortOrder };

    if (hasSearchCriteria) {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));

      // Use debounced search if debounceMs > 0, otherwise search immediately
      if (debounceMs > 0) {
        debouncedSearch(1, pagination.limit);
      } else {
        handleSearch(1, pagination.limit);
      }
    }
  }, [searchValue, filters, dateFilter, sortBy, sortOrder, pagination.limit, debouncedSearch, debounceMs, handleSearch, hasSearchCriteria]);

  /**
   * Initial fetch on mount
   */
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /**
   * Handle search button click
   */
  const handleSearchClick = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    handleSearch(1);
  };

  /**
   * Add filter with validation and trigger immediate search
   */
  const addFilter = () => {
    if (
      filterDraft.field &&
      filterDraft.value &&
      !filters.some((f) => f.field === filterDraft.field)
    ) {
      const newFilters = [...filters, filterDraft];
      setFilters(newFilters);
      setFilterDraft({ field: '', value: '' });

      // Trigger immediate search after adding filter (bypass debounce)
      setPagination((prev) => ({ ...prev, currentPage: 1 }));

      // Cancel any pending debounced search
      if (debounceMs > 0) {
        debouncedSearch.cancel();
      }

      // Trigger search immediately with the new filters
      // We need to manually update the ref since state hasn't updated yet
      searchParamsRef.current = {
        ...searchParamsRef.current,
        filters: newFilters
      };
      handleSearch(1, pagination.limit);
    }
  };

  /**
   * Remove filter by field name
   */
  const removeFilter = (field) => {
    setFilters((prev) => prev.filter((f) => f.field !== field));
  };

  /**
   * Reset all state and clear localStorage
   */
  const resetAll = () => {
    setSearchValue('');
    setFilters([]);
    setDateFilter('default');
    setSortBy('default');
    setSortOrder('default');
    setPagination({ currentPage: 1, totalPages: 1, limit: defaultLimit });
    setShowAdvancedSearch(false);
    setFilterDraft({ field: '', value: '' });

    localStorage.removeItem(`${localStoragePrefix}searchValue`);
    localStorage.removeItem(`${localStoragePrefix}advancedSearchFilters`);

    fetchAll();
  };

  return {
    // Data
    data,
    loading,
    error,
    setError,

    // Search state
    searchValue,
    setSearchValue,

    // Sort state
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,

    // Date filter
    dateFilter,
    setDateFilter,

    // Advanced search
    showAdvancedSearch,
    setShowAdvancedSearch,
    filters,
    setFilters,
    filterDraft,
    setFilterDraft,

    // Pagination
    pagination,
    setPagination,
    hasSearchCriteria,

    // Actions
    handleSearch,
    handleSearchClick,
    addFilter,
    removeFilter,
    fetchAll,
    resetAll
  };
}
